import { ADD_ELEMENT_BLOCKS } from '@/components/blocks/add-element-block-catalog';
import type { BlockStructuralRegion, PageBlock } from '@/types/block';
import type { PageSection } from '@/types/page-section';
import type { SpecialPageMeta } from '@/types/shared';

export type SpecialPageType = 'case_result' | 'blog';

export const SPECIAL_PAGE_BODY_SLOT = 'special_page_body';
export const SPECIAL_PAGE_BODY_ALLOWED_BLOCK_TYPES = ADD_ELEMENT_BLOCKS.map((d) => d.blockType);

export function getBlockSlot(block: PageBlock): string | null {
  return typeof block.props?.slot === 'string' ? block.props.slot : null;
}

export function getSpecialPageBodyGroup(section: PageSection): BlockStructuralRegion | null {
  if (!isSpecialPageLayoutSection(section)) return null;

  const blocks = section.blocks ?? [];
  const root = blocks.find((block) => getBlockSlot(block) === SPECIAL_PAGE_BODY_SLOT);
  if (!root) return null;

  return {
    rootBlockId: root.id,
    allowedBlockTypes: SPECIAL_PAGE_BODY_ALLOWED_BLOCK_TYPES,
    protectedBlockIds: [root.id],
  };
}

interface SpecialPageLayoutConfig {
  dialogTitle: string;
  dialogDescription: string;
  successToast: string;
  layoutCode: (specialPages: SpecialPageMeta | undefined) => string | null | undefined;
}

const SPECIAL_PAGE_LAYOUTS = new Map<SpecialPageType, SpecialPageLayoutConfig>([
  [
    'case_result',
    {
      dialogTitle: 'Choose a layout for your Case Result',
      dialogDescription:
        'This layout will apply to all Case Result pages. It can be changed at any time.',
      successToast: 'Case result layout updated across all case results.',
      layoutCode: (specialPages) => specialPages?.caseResultLayoutCode,
    },
  ],
  [
    'blog',
    {
      dialogTitle: 'Choose a layout for your Blog',
      dialogDescription: 'This layout will apply to all Blog pages. It can be changed at any time.',
      successToast: 'Blog layout updated across all blog posts.',
      layoutCode: (specialPages) => specialPages?.blogLayoutCode,
    },
  ],
]);

export function getSpecialPageLayoutConfig(pageType: SpecialPageType): SpecialPageLayoutConfig {
  const config = SPECIAL_PAGE_LAYOUTS.get(pageType);
  if (!config) throw new Error(`No layout config registered for pageType: ${pageType}`);
  return config;
}

export function getSpecialPageTypeForSection(section: PageSection): SpecialPageType | null {
  const sectionType = section.sectionType;
  if (sectionType && SPECIAL_PAGE_LAYOUTS.has(sectionType as SpecialPageType)) {
    return sectionType as SpecialPageType;
  }
  return null;
}

export function isSpecialPageLayoutSection(section: PageSection): boolean {
  return getSpecialPageTypeForSection(section) !== null;
}
